import { AppShell, Box, Burger, Group, NavLink, ScrollArea, Text, Title, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  IconAddressBook,
  IconBook,
  IconBox,
  IconBuilding,
  IconBuildingFactory,
  IconClipboardList,
  IconFileText,
  IconLogout,
  IconListDetails,
  IconMail,
  IconMailbox,
  IconPlug,
  IconShieldCheck,
  IconSignature,
  IconTruck,
  IconTruckLoading,
  IconUser,
  IconUserPlus,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { APP_VERSION } from '@expeditor/shared';
import { useAuthStore } from '../auth/auth.store';
import { useDemoMode } from '../demo/useDemoMode';
import { authApi } from '../api/auth';
import { ErrorBoundary } from '../ErrorBoundary';
import { EmailVerificationBanner } from './EmailVerificationBanner';

/**
 * Layout кабинета клиента: шапка + слева навигация по разделам, справа — контент.
 */
export function CabinetLayout() {
  const [opened, { toggle }] = useDisclosure();
  const loc = useLocation();
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);

  const handleLogout = async () => {
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // ignore — токены чистим в любом случае
      }
    }
    clear();
    nav('/login', { replace: true });
  };

  const demoMode = useDemoMode();
  const isClient = user?.role === 'CLIENT';
  const isStaff = user && ['MANAGER', 'ADMIN', 'AUDITOR'].includes(user.role);
  const canAudit = user && ['ADMIN', 'AUDITOR'].includes(user.role);
  const isAdmin = user?.role === 'ADMIN';

  const clientLinks = [
    { to: '/cabinet/profile', label: 'Профиль', icon: <IconUser size={18} /> },
    { to: '/cabinet/orders', label: 'Мои поручения', icon: <IconClipboardList size={18} /> },
    // «Мои контракты» (per-поручение контракты от акцепта котировок) скрыты из меню —
    // остаются в БД/у менеджера для ПП 1317. Рамочные договоры ТЭО заводит менеджер.
    { to: '/cabinet/shipments', label: 'Мои перевозки', icon: <IconTruckLoading size={18} /> },
    { to: '/cabinet/addresses', label: 'Мои адреса', icon: <IconAddressBook size={18} /> },
    { to: '/cabinet/cargo', label: 'Мои грузы', icon: <IconBox size={18} /> },
    { to: '/cabinet/counterparties', label: 'Мои контрагенты', icon: <IconUsers size={18} /> },
    // «Об экспедиторе» убрано из меню: реквизиты экспедитора и договоры ТЭО
    // показываются в профиле клиента. Маршрут /cabinet/expeditor оставлен
    // рабочим — на него могут вести старые ссылки.
  ];
  const staffLinks = [
    { to: '/manager/clients', label: 'Все клиенты', icon: <IconBuildingFactory size={18} /> },
    { to: '/manager/orders', label: 'Все поручения', icon: <IconClipboardList size={18} /> },
    { to: '/manager/contracts', label: 'Все контракты', icon: <IconSignature size={18} /> },
    { to: '/manager/shipments', label: 'Все перевозки', icon: <IconTruckLoading size={18} /> },
    { to: '/manager/carriers', label: 'Привлекаемые исполнители', icon: <IconTruck size={18} /> },
    { to: '/manager/expeditions', label: 'Поручения ЭДО', icon: <IconClipboardList size={18} /> },
    { to: '/manager/expeditor', label: 'Реквизиты экспедитора', icon: <IconBuilding size={18} /> },
  ];
  const adminLinks = isAdmin
    ? [
        { to: '/manager/staff', label: 'Сотрудники', icon: <IconUsersGroup size={18} /> },
        { to: '/manager/mailer', label: 'Настройки SMTP', icon: <IconMail size={18} /> },
        { to: '/manager/mailer-log', label: 'Журнал писем', icon: <IconMailbox size={18} /> },
        { to: '/manager/integrations', label: 'Интеграции', icon: <IconPlug size={18} /> },
        {
          to: '/manager/service-catalog',
          label: 'Доп. услуги',
          icon: <IconListDetails size={18} />,
        },
        {
          to: '/manager/poruchenie-template',
          label: 'Печатная форма поручения',
          icon: <IconFileText size={18} />,
        },
        {
          to: '/manager/reference-data',
          label: 'Справочники',
          icon: <IconBook size={18} />,
        },
      ]
    : [];
  const auditLinks = canAudit
    ? [{ to: '/audit', label: 'Проверка ПП 1317', icon: <IconShieldCheck size={18} /> }]
    : [];
  // ДЕМО-СТЕНД: сотрудник (MANAGER) может завести демо-клиента, чтобы попробовать
  // сторону заказчика. Показываем только в демо-режиме.
  const demoLinks =
    demoMode && user?.role === 'MANAGER'
      ? [
          {
            to: '/manager/demo-client',
            label: 'Создать демо-клиента',
            icon: <IconUserPlus size={18} />,
          },
        ]
      : [];
  const links = isClient
    ? clientLinks
    : isStaff
    ? [...staffLinks, ...demoLinks, ...adminLinks, ...auditLinks]
    : auditLinks;

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="xs">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Box
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: 'var(--mantine-color-brand-6)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              Э
            </Box>
            <Title order={4}>ИС экспедитора</Title>
          </Group>
          <Group>
            {user && (
              <Text size="sm" c="dimmed">
                {user.displayName ?? user.email} · {user.role}
              </Text>
            )}
            <Button
              variant="subtle"
              component={Link}
              to={isClient ? '/cabinet/guide' : '/manager/guide'}
              leftSection={<IconBook size={16} />}
            >
              Руководство
            </Button>
            <Button
              variant="subtle"
              leftSection={<IconLogout size={16} />}
              onClick={handleLogout}
            >
              Выйти
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <ScrollArea style={{ flex: 1 }}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              component={Link}
              to={l.to}
              label={l.label}
              leftSection={l.icon}
              active={loc.pathname === l.to}
            />
          ))}
        </ScrollArea>
        {/* Версия сервиса — чтобы при обращении в поддержку было видно, что стоит. */}
        <Box p="xs">
          <Text size="xs" c="dimmed" ta="center">
            Версия {APP_VERSION}
          </Text>
        </Box>
      </AppShell.Navbar>

      <AppShell.Main>
        <EmailVerificationBanner />
        <ErrorBoundary key={loc.pathname}>
          <Outlet />
        </ErrorBoundary>
      </AppShell.Main>
    </AppShell>
  );
}
